import { useMemo, useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Zap,
  ArrowLeft,
  Search,
  AlertTriangle,
  Info,
  Filter,
  X,
  BookOpen,
  ShieldAlert,
  Radio,
  StickyNote,
  Clock,
} from "lucide-react";
import PnjLogo from "@/components/PnjLogo";
import data from "@/data/power_guidance.json";

/**
 * PowerGuidance — pencarian failure code SCADA/Power.
 * Sumber: `data/power_guidance.json` di-generate dari sheet "New Version"
 * pada Powerapps FAILURE GUIDANCE POWER SCADA.xlsx (253 entries × 9 tipe).
 *
 * Field yang ditampilkan pada detail:
 *   Failure Type · Information Failure · Cause · Alarm Level · Impact ·
 *   Handling · Note
 */
const ENTRIES = data.entries || [];
const TYPES = Array.from(
  new Set(ENTRIES.map((e) => e.type).filter(Boolean)),
).sort();

const highlight = (text, needle) => {
  if (!needle || !text) return text;
  const idx = text.toLowerCase().indexOf(needle.toLowerCase());
  if (idx < 0) return text;
  return (
    <>
      {text.slice(0, idx)}
      <span className="bg-[#f97316]/40 text-white rounded px-0.5">
        {text.slice(idx, idx + needle.length)}
      </span>
      {text.slice(idx + needle.length)}
    </>
  );
};

const LevelBadge = ({ level }) => {
  const norm = (level || "").toLowerCase();
  let cls = "bg-slate-500/20 text-slate-300 border-slate-500/40";
  let label = level || "—";
  if (norm === "high") {
    cls = "bg-[#CA2047]/20 text-[#ff8fa3] border-[#CA2047]/40";
    label = "HIGH";
  } else if (norm === "low") {
    cls = "bg-[#f59e0b]/20 text-[#fbbf24] border-[#f59e0b]/40";
    label = "LOW";
  } else if (norm === "x") {
    cls = "bg-slate-500/15 text-slate-400 border-slate-500/30";
    label = "N/A";
  }
  return (
    <span
      className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded border ${cls}`}
    >
      {label}
    </span>
  );
};

const DetailPanel = ({ entry }) => {
  if (!entry) return null;
  const rows = [
    { key: "info", label: "Information Failure", icon: Info, color: "text-[#7dd3fc]", value: entry.info },
    { key: "cause", label: "Cause", icon: AlertTriangle, color: "text-[#f472b6]", value: entry.cause },
    { key: "impact", label: "Impact", icon: Radio, color: "text-[#fbbf24]", value: entry.impact },
    { key: "handling", label: "Handling", icon: BookOpen, color: "text-[#22c55e]", value: entry.handling },
    { key: "note", label: "Note", icon: StickyNote, color: "text-slate-400", value: entry.note },
  ];
  return (
    <div
      data-testid="detail-panel"
      className="rounded-xl border border-[#f97316]/40 bg-gradient-to-br from-[#0d243a] to-[#062036] p-4 md:p-5 shadow-lg shadow-orange-500/10"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#fbbf24]">
            {entry.type} · #{entry.no}
          </div>
          <h3 className="mt-0.5 font-display text-lg md:text-xl font-bold leading-tight">
            {entry.code}
          </h3>
          <div className="mt-0.5 text-[11px] font-mono text-slate-400">
            Failure Type:{" "}
            <span className="text-[#facc15] font-semibold text-sm">
              {entry.type || "—"}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest text-slate-500">
            Alarm Level:
          </span>
          <LevelBadge level={entry.level} />
        </div>
      </div>

      <div className="mt-3 grid gap-3 md:grid-cols-2">
        {rows
          .filter((r) => r.value && r.value.length > 0)
          .map((r) => {
            const Icon = r.icon;
            return (
              <div
                key={r.key}
                className="rounded-lg border border-[#243a52] bg-[#0b1a29] p-3"
              >
                <div
                  className={`flex items-center gap-2 text-[11px] uppercase tracking-widest mb-1.5 ${r.color}`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {r.label}
                </div>
                <div className="text-xs md:text-sm text-slate-100 leading-relaxed whitespace-pre-line">
                  {r.value}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default function PowerGuidance() {
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [selected, setSelected] = useState(null);
  const [recent, setRecent] = useState([]);

  // Load recent searches (codes) from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem("pg_recent_code");
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) setRecent(arr.slice(0, 5));
      }
    } catch {
      /* ignore */
    }
  }, []);

  const pushRecent = useCallback((code) => {
    if (!code) return;
    setRecent((prev) => {
      const next = [code, ...prev.filter((x) => x !== code)].slice(0, 5);
      try {
        localStorage.setItem("pg_recent_code", JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const clearRecent = () => {
    setRecent([]);
    try {
      localStorage.removeItem("pg_recent_code");
    } catch {
      /* ignore */
    }
  };

  const hasFilter = typeFilter !== "ALL";
  const hasQuery = query.trim().length > 0;

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = ENTRIES;
    if (hasFilter) list = list.filter((e) => e.type === typeFilter);
    if (!q) {
      // Filter-only browse mode: tampilkan seluruh entries dari tipe terpilih.
      return list
        .slice()
        .sort((a, b) => Number(a.no) - Number(b.no))
        .slice(0, 100);
    }
    const scored = list
      .map((e) => {
        const code = (e.code || "").toLowerCase();
        const type = (e.type || "").toLowerCase();
        const info = (e.info || "").toLowerCase();
        const cause = (e.cause || "").toLowerCase();
        let score = 0;
        if (code === q) score = 100;
        else if (code.startsWith(q)) score = 80;
        else if (code.includes(q)) score = 60;
        else if (type.includes(q)) score = 40;
        else if (info.includes(q)) score = 30;
        else if (cause.includes(q)) score = 20;
        return { e, score };
      })
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score);
    return scored.map((x) => x.e).slice(0, 40);
  }, [query, typeFilter, hasFilter]);

  const perfectMatch = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    return ENTRIES.find((e) => (e.code || "").toLowerCase() === q) || null;
  }, [query]);

  const active = selected || perfectMatch;

  // Tercatat sebagai recent saat user membuka detail
  useEffect(() => {
    if (active?.code) pushRecent(active.code);
  }, [active, pushRecent]);

  const applyRecent = (code) => {
    setQuery(code);
    setSelected(null);
  };

  const isIdle = !hasQuery && !hasFilter && !active;
  const showResults = hasQuery || hasFilter;

  return (
    <div
      className="min-h-screen bg-[#0c1b2a] text-white relative overflow-hidden"
      data-testid="page-power-guidance"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 opacity-25 pg-grid" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(14,165,233,0.14),transparent_55%)]" />
      </div>

      <header className="relative z-10 max-w-6xl mx-auto flex items-center justify-between px-6 pt-8">
        <Link
          to="/failure-guidance"
          className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors"
          data-testid="back-link"
        >
          <ArrowLeft className="h-4 w-4" />
          Failure Guidance
        </Link>
        <PnjLogo size={32} />
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-6 pt-5 pb-10">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#f97316] to-[#facc15] flex items-center justify-center shadow-lg shadow-orange-500/30">
            <Zap className="h-6 w-6 text-[#0c1b2a]" strokeWidth={2.4} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#fbbf24]">
              Power SCADA · {ENTRIES.length} entries
            </div>
            <h1 className="font-display text-xl md:text-2xl font-extrabold leading-tight">
              Power Guidance List
            </h1>
          </div>
        </div>

        <div className="rounded-xl border border-[#243a52] bg-[#0b1a29]/70 p-3 md:p-4">
          <div className="flex flex-col md:flex-row gap-2.5">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setSelected(null);
                }}
                placeholder="Ketik failure code (mis. UV 27P1, RTU HUB Failure, OC 50P1)..."
                data-testid="pg-search-input"
                className="w-full pl-10 pr-10 py-2.5 rounded-lg bg-[#0c1b2a] border border-[#243a52] focus:border-[#f97316] focus:outline-none focus:ring-1 focus:ring-[#f97316] text-sm font-mono placeholder:text-slate-500"
                autoFocus
              />
              {query && (
                <button
                  onClick={() => setQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-[#243a52]"
                  aria-label="Clear"
                  data-testid="pg-clear"
                >
                  <X className="h-4 w-4 text-slate-400" />
                </button>
              )}
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                data-testid="pg-type-filter"
                className="w-full md:w-52 pl-10 pr-4 py-2.5 rounded-lg bg-[#0c1b2a] border border-[#243a52] focus:border-[#f97316] focus:outline-none text-sm appearance-none cursor-pointer"
              >
                <option value="ALL">Semua tipe</option>
                {TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-400">
            <Info className="h-3 w-3" />
            <span>
              Ketik <span className="font-mono text-[#facc15]">failure code</span>{" "}
              lengkap untuk detail langsung, atau ketik sebagian untuk daftar
              usulan.
            </span>
          </div>
        </div>

        {active && (
          <div className="mt-4">
            <DetailPanel entry={active} />
          </div>
        )}

        {/* Result list — muncul bila ada query ATAU filter dipilih */}
        {showResults && (
          <div className="mt-4">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">
              {hasQuery
                ? `Hasil pencarian (${results.length})`
                : `${typeFilter} · ${results.length} entries`}
            </div>

            {results.length === 0 && (
              <div
                className="rounded-lg border border-dashed border-[#CA2047]/40 bg-[#CA2047]/5 p-4 flex items-start gap-3"
                data-testid="pg-empty"
              >
                <ShieldAlert className="h-5 w-5 text-[#ff8fa3] shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-[#ff8fa3] text-sm">
                    Failure code tidak ditemukan
                  </div>
                  <div className="text-xs text-slate-300 mt-0.5">
                    Coba ubah kata kunci atau ganti filter tipe.
                  </div>
                </div>
              </div>
            )}

            <div className="grid gap-1.5" data-testid="pg-results">
              {results.map((e) => {
                const isActive = active && active.code === e.code && active.no === e.no;
                return (
                  <button
                    key={`${e.no}-${e.code}`}
                    onClick={() => setSelected(e)}
                    data-testid={`pg-result-${e.no}`}
                    className={`text-left rounded-lg border px-3 py-2 transition-all hover:-translate-y-0.5 ${
                      isActive
                        ? "border-[#f97316]/70 bg-[#062036] shadow-md shadow-orange-500/10"
                        : "border-[#243a52] bg-[#0b1a29]/70 hover:border-[#f97316]/40"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono font-bold text-[#facc15] text-sm">
                            {highlight(e.code, query)}
                          </span>
                          <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-[#122032] text-slate-400">
                            {e.type}
                          </span>
                          <span className="text-[9px] font-mono text-slate-500">
                            #{e.no}
                          </span>
                        </div>
                        <div className="mt-0.5 text-xs text-slate-200 truncate">
                          {highlight(e.info || "—", query)}
                        </div>
                      </div>
                      <LevelBadge level={e.level} />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Idle: prompt + recent searches chips */}
        {isIdle && (
          <div
            className="mt-4 rounded-lg border border-dashed border-[#243a52] bg-[#0b1a29]/50 p-6"
            data-testid="pg-idle"
          >
            <div className="text-center">
              <Search className="h-6 w-6 text-slate-500 mx-auto mb-2" />
              <div className="text-sm text-slate-300">
                Mulai mengetik atau pilih tipe untuk mencari{" "}
                <span className="font-mono text-[#facc15]">failure code</span>{" "}
                Power SCADA
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                {ENTRIES.length} entries · {TYPES.length} tipe failure
              </div>
            </div>

            {recent.length > 0 && (
              <div className="mt-5 pt-4 border-t border-[#243a52]" data-testid="pg-recent">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500">
                    <Clock className="h-3 w-3" />
                    Recent Searches
                  </div>
                  <button
                    onClick={clearRecent}
                    data-testid="pg-recent-clear"
                    className="text-[10px] uppercase tracking-widest text-slate-500 hover:text-[#ff8fa3]"
                  >
                    Bersihkan
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {recent.map((code) => (
                    <button
                      key={code}
                      onClick={() => applyRecent(code)}
                      data-testid={`pg-recent-${code}`}
                      className="text-xs font-mono px-2.5 py-1 rounded-full border border-[#f97316]/40 bg-[#f97316]/10 text-[#fbbf24] hover:bg-[#f97316]/20 transition-colors"
                    >
                      {code}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <footer className="mt-10 pt-4 border-t border-[#243a52] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-[11px] text-slate-500">
          <div>
            <span className="text-slate-400 font-display font-semibold">
              CRLS Console Suite
            </span>{" "}
            · Power Guidance · v1.0
          </div>
          <PnjLogo size={28} />
        </footer>
      </main>

      <style>{`
        .pg-grid {
          background-image:
            linear-gradient(rgba(14,165,233,0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(14,165,233,0.05) 1px, transparent 1px);
          background-size: 42px 42px;
        }
      `}</style>
    </div>
  );
}
